import { DateTimePicker } from '@mui/lab';
import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useEffect, useState } from 'react';
import useToggle from '../../../../hooks/useToggle';
import BeaconRte, {
  convertBeaconRteToHtml,
  EditorValueHasText,
  EmptyRteValue,
} from '../../../../shared-components/BeaconRte';
import InstitutionContext from '../../../../shared-components/contexts/InstitutionContext';
import NotationAttributeContext from '../../../../shared-components/contexts/NotationAttributesContext';
import UserContext from '../../../../shared-components/contexts/UserContext';
import OutlinedFormControl from '../../../../shared-components/OutlinedFormControl';
import UserSearch from '../../../../shared-components/UserSearch';
import PrivacyPolicy from '../../../../shared-components/PrivacyPolicy';
import { IMultiStudentCreateAlertRequest } from '../../../../shared-interfaces/Notation/Alert/IMultiStudentCreateAlertRequest';
import { IMultiStudentCreateEncouragementRequest } from '../../../../shared-interfaces/Notation/Encouragement/IMultiStudentCreateEncouragementRequest';
import { ICategory } from '../../../../shared-interfaces/Notation/ICategory';
import { IEncouragementType } from '../../../../shared-interfaces/Notation/IEncouragementType';
import { IReason } from '../../../../shared-interfaces/Notation/IReason';
import { IMultiStudentCreateUpdateNoteRequest } from '../../../../shared-interfaces/Notation/UpdateNote/IMultiStudentCreateUpdateNoteRequest';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../../../shared-interfaces/User/ICoreUser';
import UserService from '../../../../shared-services/User/UserService';
import AudienceType from '../../../../shared-types/AudienceType';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import { Role } from '../../../../shared-types/Roles';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    //@ts-ignore
    [theme.breakpoints.down('md')]: {
      minWidth: '300px',
    },
    minWidth: '600px',
  },
  formControl: {
    width: '100%',
    marginBottom: '10px',
  },
}));

interface ICreateNotationForm {
  notationType: NotationType;

  onSave: (
    request:
      | IMultiStudentCreateAlertRequest
      | IMultiStudentCreateEncouragementRequest
      | IMultiStudentCreateUpdateNoteRequest,
  ) => void;
  onCancel: () => void;
}

const CreateNotationForm = (props: ICreateNotationForm) => {
  const MAX_CREATE_FOR = 10;
  const MAX_CREATE_FOR_ALERT = 25;

  const classes = useStyles();
  const institution = useContext(InstitutionContext);
  const user = useContext(UserContext);
  const notationContext = useContext(NotationAttributeContext);

  const [isSaving, setIsSaving] = useState(false);
  const [studentNetworkIsLoading, toggleStudentNetworkIsLoading] =
    useToggle(false);

  const [validCreateFor, setValidCreateFor] = useState(true);
  const [validAudience, setValidAudience] = useState(true);
  const [validDescription, setValidDescription] = useState(true);
  const [validEncouragement, setValidEncouragement] = useState(true);

  const [studentNetwork, setStudentNetwork] = useState<ICoreUser[]>([]);

  // Form State
  const [createFor, setCreateFor] = useState<ICoreUser[]>([]);
  const [audience, setAudience] = useState<AudienceType>(AudienceType.none);
  const [specificNetworkMember, setSpecificNetworkMember] =
    useState<ICoreUser>();
  const [additionalAudience, setAdditionalAudience] = useState<ICoreUser[]>([]);
  const [assignedTo, setAssignedTo] = useState<ICoreUser>();
  const [occurrenceDate, setOccurrenceDate] = useState<Date>();
  const [sendStudentNotation, toggleSendStudentNotation] = useToggle(false);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [reasons, setReasons] = useState<IReason[]>([]);
  const [encouragementType, setEncouragementType] =
    useState<IEncouragementType>();
  const [description, setDescription] = useState(EmptyRteValue);

  useEffect(() => {
    if (
      createFor.length !== 1 &&
      audience === AudienceType.specificNetworkMembers
    ) {
      setAudience(AudienceType.none);
    }

    if (createFor && createFor.length === 1) {
      toggleStudentNetworkIsLoading();
      UserService.getStudentNetwork((createFor as ICoreUser[])[0].id)
        .then(u => setStudentNetwork(u))
        .finally(() => toggleStudentNetworkIsLoading());
    } else {
      setStudentNetwork([]);
      setSpecificNetworkMember(undefined);
    }
  }, [createFor]);

  function validateForm() {
    var audienceValid = true;

    if (
      createFor.length === 0 ||
      createFor.length >
        (props.notationType === NotationType.Alert
          ? MAX_CREATE_FOR_ALERT
          : MAX_CREATE_FOR)
    ) {
      setValidCreateFor(false);
    }

    if (audience === AudienceType.none) {
      setValidAudience(false);
      audienceValid = false;
    } else if (
      audience === AudienceType.specificNetworkMembers &&
      !specificNetworkMember
    ) {
      setValidAudience(false);
      audienceValid = false;
    }

    if (!description || (description && !description)) {
      setValidDescription(false);
    }

    if (props.notationType === NotationType.Encouragement) {
      if (!encouragementType) setValidEncouragement(false);
      // Checking the state vars can hit a race condition
      return (
        createFor.length > 0 &&
        createFor.length <= MAX_CREATE_FOR &&
        audienceValid &&
        description &&
        EditorValueHasText(description) &&
        encouragementType
      );
    } else {
      // Checking the state vars can hit a race condition
      return (
        createFor.length > 0 &&
        createFor.length <=
          (props.notationType === NotationType.Alert
            ? MAX_CREATE_FOR_ALERT
            : MAX_CREATE_FOR) &&
        audienceValid &&
        description &&
        EditorValueHasText(description)
      );
    }
  }

  function resetForm() {
    setCreateFor([]);
    setAudience(AudienceType.none);
    setSpecificNetworkMember(undefined);
    setAdditionalAudience([]);
    setOccurrenceDate(undefined);
    setCategories([]);
    setReasons([]);
    setDescription(EmptyRteValue());
    setEncouragementType(undefined);

    setIsSaving(false);
  }

  function createRequestObject() {
    if (props.notationType === NotationType.Update) {
      return {
        studentIds: (createFor as ICoreUser[]).map(c => c.id),
        details: convertBeaconRteToHtml(description),
        canStudentView: sendStudentNotation,
        audienceType: audience,
        audienceMembers: specificNetworkMember
          ? [specificNetworkMember.id]
          : [],
        additionalAudienceMembers: additionalAudience.map(u => u.id),
        selectedReasonIds: reasons.map(c => c.id),
        selectedCategoryIds: categories.map(c => c.id),
        occurenceDate: occurrenceDate,
        institutionId: institution.institution.id,
      } as IMultiStudentCreateUpdateNoteRequest;
    } else if (props.notationType === NotationType.Alert) {
      return {
        studentIds: (createFor as ICoreUser[]).map(c => c.id),
        details: convertBeaconRteToHtml(description),
        canStudentView: sendStudentNotation,
        audienceType: audience,
        audienceMembers: specificNetworkMember
          ? [specificNetworkMember.id]
          : [],
        additionalAudienceMembers: additionalAudience.map(u => u.id),
        selectedReasonIds: reasons.map(c => c.id),
        selectedCategoryIds: categories.map(c => c.id),
        occurenceDate: occurrenceDate,
        institutionId: institution.institution.id,
        assignedToId: assignedTo ? assignedTo.id : undefined,
      } as IMultiStudentCreateAlertRequest;
    } else {
      return {
        studentIds: (createFor as ICoreUser[]).map(c => c.id),
        details: convertBeaconRteToHtml(description),
        audienceType: audience,
        typeId: encouragementType?.id,
        institutionId: institution.institution.id,
      } as IMultiStudentCreateEncouragementRequest;
    }
  }

  const AudienceFormControl = () => {
    return (
      <div style={{ marginBottom: '10px' }}>
        <OutlinedFormControl
          label="Audience"
          style={{ width: '100%' }}
          required
          error={!validAudience}
        >
          <RadioGroup
            aria-label="Audience"
            name="audience"
            value={audience}
            onChange={v => {
              setAudience(v.target.value as AudienceType);
              setValidAudience(true);
            }}
            onBlur={() => {
              if (audience !== AudienceType.none) setValidAudience(true);
              else setValidAudience(true);
            }}
          >
            {(props.notationType === NotationType.Update ||
              props.notationType === NotationType.Encouragement) && (
              <FormControlLabel
                value={AudienceType.private}
                control={<Radio />}
                label="Private"
              />
            )}
            {(props.notationType === NotationType.Alert ||
              props.notationType === NotationType.Update) && (
              <FormControlLabel
                value={AudienceType.professionalStaff}
                control={<Radio />}
                label="Only Professional Staff"
              />
            )}
            <FormControlLabel
              value={AudienceType.prefessionalStaffPeerAdvisors}
              control={<Radio />}
              label="Professional Staff + Peer Advisor"
            />
            {(props.notationType === NotationType.Alert ||
              props.notationType === NotationType.Update) && (
              <Tooltip
                title={
                  createFor.length > 1
                    ? 'Specific Staff Members can only be selected for one student'
                    : createFor.length === 0
                    ? 'Please select a student to see their network'
                    : ''
                }
                arrow
                placement="top"
              >
                <span>
                  <FormControlLabel
                    value={AudienceType.specificNetworkMembers}
                    control={<Radio />}
                    label="Specific Staff Member"
                    disabled={createFor.length === 0 || createFor.length > 1}
                  />
                </span>
              </Tooltip>
            )}
          </RadioGroup>

          <Collapse in={audience === AudienceType.specificNetworkMembers}>
            <FormControl style={{ width: '100%' }}>
              <InputLabel
                id="select-specific-network-member-label"
                style={{ marginLeft: '15px' }}
              >
                {!studentNetworkIsLoading && studentNetwork.length === 0
                  ? 'No Staff Members Available'
                  : 'Select a Staff Member'}
              </InputLabel>
              <Select
                labelId="select-specific-network-member-label"
                id="select-specific-network-member"
                variant="outlined"
                value={specificNetworkMember}
                endAdornment={studentNetworkIsLoading && <CircularProgress />}
                disabled={
                  !studentNetworkIsLoading && studentNetwork.length === 0
                }
                renderValue={() => {
                  if (specificNetworkMember)
                    return GetCoreUserFormattedName(
                      specificNetworkMember,
                      false,
                    );
                  else return undefined;
                }}
                style={{ margin: '5px 10px 7px 0' }}
                displayEmpty
              >
                {studentNetwork.map((e, i) => (
                  <MenuItem
                    key={e.id}
                    value={e.id}
                    onClick={() => setSpecificNetworkMember(e)}
                  >
                    {GetCoreUserFormattedName(e, false)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Collapse>
        </OutlinedFormControl>
        {!validAudience && (
          <Typography variant="caption" color="error">
            Audience is required.
          </Typography>
        )}

        {institution.settings.sendStudentsNotifications &&
          (props.notationType === NotationType.Alert ||
            props.notationType === NotationType.Update) && (
            <FormControlLabel
              sx={{ padding: '8px 0 0 8px' }}
              control={
                <Checkbox
                  checked={sendStudentNotation}
                  onChange={() => toggleSendStudentNotation()}
                  inputProps={{
                    'aria-label': `Allow student to view ${props.notationType.toLowerCase()}`,
                  }}
                />
              }
              label={`Allow student to view ${props.notationType.toLowerCase()}`}
            />
          )}
      </div>
    );
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <form>
          <FormControl className={classes.formControl} required={true}>
            <UserSearch
              label={`Name or External Id of Student(s) (Max ${
                props.notationType === NotationType.Alert
                  ? MAX_CREATE_FOR_ALERT
                  : MAX_CREATE_FOR
              })*`}
              onSelect={u => setCreateFor(u as ICoreUser[])}
              roleRestriction={[Role.Student, Role.PeerAdvisor]}
              value={createFor}
              maxSearchLength={
                props.notationType === NotationType.Alert
                  ? MAX_CREATE_FOR_ALERT
                  : MAX_CREATE_FOR
              }
              multiple
              onBlur={() => {
                if (createFor.length === 0) setValidCreateFor(false);
                else setValidCreateFor(true);
              }}
              error={!validCreateFor}
              inNetwork={!user.isGeneralist}
            />
            {!validCreateFor && createFor.length === 0 && (
              <Typography variant="caption" color="error">
                One or more Students required.
              </Typography>
            )}
            {createFor.length >
              (props.notationType === NotationType.Alert
                ? MAX_CREATE_FOR_ALERT
                : MAX_CREATE_FOR) && (
              <Typography color="error">
                {`Alerts can only be created for up to ${
                  props.notationType === NotationType.Alert
                    ? MAX_CREATE_FOR_ALERT
                    : MAX_CREATE_FOR
                } students at a time.`}
              </Typography>
            )}
          </FormControl>

          <AudienceFormControl />

          {(props.notationType === NotationType.Alert ||
            props.notationType === NotationType.Update) && (
            <>
              <FormControl className={classes.formControl}>
                <UserSearch
                  label="Additional Audience"
                  multiple
                  disabled={createFor.length === 0}
                  value={additionalAudience}
                  onSelect={u => setAdditionalAudience(u as ICoreUser[])}
                  assignableTo={
                    createFor.length === 1 ? createFor[0].id : undefined
                  }
                  roleRestriction={
                    createFor.length > 1
                      ? [Role.Generalist, Role.Executive, Role.Administrator]
                      : [
                          Role.PeerAdvisor,
                          Role.Advisor,
                          Role.Generalist,
                          Role.Executive,
                          Role.Administrator,
                        ]
                  }
                />
              </FormControl>
              {props.notationType === NotationType.Alert && (
                <FormControl className={classes.formControl}>
                  <UserSearch
                    label="Assign To"
                    disabled={createFor.length === 0}
                    value={assignedTo}
                    onSelect={u => setAssignedTo(u as ICoreUser)}
                    assignableTo={
                      createFor.length === 1 ? createFor[0].id : undefined
                    }
                    roleRestriction={
                      createFor.length > 1
                        ? [Role.Generalist, Role.Executive, Role.Administrator]
                        : [
                            Role.PeerAdvisor,
                            Role.Advisor,
                            Role.Generalist,
                            Role.Executive,
                            Role.Administrator,
                          ]
                    }
                  />
                </FormControl>
              )}

              <FormControl className={classes.formControl}>
                <DateTimePicker
                  renderInput={props => <TextField fullWidth {...props} />}
                  clearable
                  label="Occurrence Date"
                  value={occurrenceDate ?? null}
                  maxDateTime={new Date()}
                  onChange={date =>
                    setOccurrenceDate(date !== null ? date : undefined)
                  }
                />
              </FormControl>

              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="categories-label">Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  id="categories"
                  value={categories}
                  label="Categories"
                  multiple
                  inputProps={{ 'aria-labelledby': 'categories-label' }}
                  renderValue={() => {
                    return categories
                      .sort((c1, c2) => (c1.name > c2.name ? 1 : -1))
                      .map(c => c.name)
                      .join(', ');
                  }}
                >
                  {notationContext.categories.map(x => (
                    <MenuItem
                      key={x.id}
                      value={x.id}
                      onClick={() => {
                        if (categories.indexOf(x) > -1) {
                          setCategories(categories.filter(c => c.id !== x.id));
                        } else {
                          setCategories([x, ...categories]);
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox checked={categories.indexOf(x) !== -1} />
                      </ListItemIcon>
                      <ListItemText primary={x.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="reasons-label">Reasons</InputLabel>
                <Select
                  labelId="reasons-label"
                  id="reasons"
                  value={reasons}
                  label="Reasons"
                  multiple
                  inputProps={{ 'aria-labelledby': 'reasons-label' }}
                  renderValue={() => {
                    return reasons
                      .sort((r1, r2) => (r1.name > r2.name ? 1 : -1))
                      .map(c => c.name)
                      .join(', ');
                  }}
                >
                  {notationContext.reasons.map(x => (
                    <MenuItem
                      key={x.id}
                      value={x.id}
                      onClick={() => {
                        if (reasons.indexOf(x) > -1) {
                          setReasons(reasons.filter(c => c.id !== x.id));
                        } else {
                          setReasons([x, ...reasons]);
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox checked={reasons.indexOf(x) !== -1} />
                      </ListItemIcon>
                      <ListItemText primary={x.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {props.notationType === NotationType.Encouragement && (
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="encouragement-type-label">
                Encouragement Type*
              </InputLabel>
              <Select
                labelId="encouragement-type-label"
                id="encouragement-type"
                value={encouragementType}
                label="Encouragement Type"
                inputProps={{ 'aria-labelledby': 'encouragement-type-label' }}
                renderValue={() => encouragementType?.name}
                error={!validEncouragement}
                onBlur={() => {
                  if (encouragementType !== undefined)
                    setValidEncouragement(true);
                  else setValidEncouragement(false);
                }}
                required
              >
                {notationContext.encouragementTypes.map(x => (
                  <MenuItem
                    key={x.id}
                    value={x.id}
                    onClick={() => setEncouragementType(x)}
                  >
                    <ListItemText primary={x.name} />
                  </MenuItem>
                ))}
              </Select>
              {!validEncouragement && (
                <Typography variant="caption" color="error">
                  Encouragement Type is required.
                </Typography>
              )}
            </FormControl>
          )}
          <BeaconRte
            label="Description"
            onChange={e => {
              setDescription(e);
            }}
            value={description}
            error={!validDescription}
            onBlur={hasText => {
              if (hasText) setValidDescription(true);
              else setValidDescription(false);
            }}
            required
          />
          {!validDescription && (
            <Typography variant="caption" color="error">
              Description is required.
            </Typography>
          )}
        </form>
        <PrivacyPolicy policyHtml={notationContext.assessmentFerpaStatement} />
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          disabled={isSaving}
          onClick={() => {
            resetForm();
            props.onCancel();
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isSaving}
          onClick={() => {
            if (validateForm()) {
              setIsSaving(true);
              props.onSave(createRequestObject());
            }
          }}
        >
          Create {props.notationType}
        </Button>
      </DialogActions>
    </>
  );
};
export default CreateNotationForm;
